import React from 'react';
import { formatCurrencyARS } from './numberFormat';

interface TableData {
  [key: string]: any;
}

export type ParsedTag = {
  key?: string;
  value: string;
};

export const EXCLUDED_TAG_KEYS = new Set(['id', 'marca', 'category', 'size']);
export const EXCLUDED_TAG_VALUES = new Set(['id', 'marca', 'category', 'size']);
export const UNIT_PRICE_CLIENT_ID = 'MPA8iPXNzLrLyTLuiDv3';
export const TIA_ECUADOR_CLIENT_ID = 'O5FwXMCOSQUOPmKKN9JU';
export const MPU_KEYS = ['MPU', 'mpu', 'm_pu', 'mpu_value', 'MPU_VALUE'];
export const MPUM_KEYS = ['MPUM', 'mpum', 'm_pum', 'mpum_value', 'MPU_M', 'MPUM_VALUE'];

export const normalizeFieldName = (field: string): string =>
  field
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const parseRawTags = (rawValue: unknown): ParsedTag[] => {
  const tags: ParsedTag[] = [];
  const seen = new Set<string>();

  const pushTag = (key: string | null | undefined, value: unknown) => {
    if (value === null || value === undefined) {
      return;
    }
    const valueStr = String(value).trim();
    if (!valueStr) {
      return;
    }
    let normalizedKey = key ? key.trim() : '';
    let normalizedKeyLower = normalizedKey.toLowerCase();

    if (normalizedKeyLower && EXCLUDED_TAG_KEYS.has(normalizedKeyLower)) {
      return;
    }

    if (normalizedKeyLower === 'tag') {
      normalizedKey = '';
      normalizedKeyLower = '';
    }

    let processedValue = valueStr;
    let processedValueLower = processedValue.toLowerCase();

    if (!normalizedKeyLower) {
      const separatorIndex = (() => {
        const colonIndex = processedValue.indexOf(':');
        if (colonIndex !== -1) return colonIndex;
        const equalIndex = processedValue.indexOf('=');
        if (equalIndex !== -1) return equalIndex;
        return -1;
      })();

      if (separatorIndex > -1) {
        const candidateKey = processedValue.slice(0, separatorIndex).trim();
        const candidateKeyLower = candidateKey.toLowerCase();
        const candidateValue = processedValue.slice(separatorIndex + 1).trim();
        const candidateValueLower = candidateValue.toLowerCase();

        if (candidateKeyLower && EXCLUDED_TAG_KEYS.has(candidateKeyLower)) {
          return;
        }

        if (EXCLUDED_TAG_VALUES.has(candidateValueLower)) {
          return;
        }

        if (candidateKeyLower === 'tag') {
          processedValue = candidateValue;
          processedValueLower = candidateValueLower;
        } else {
          normalizedKey = candidateKey;
          normalizedKeyLower = candidateKeyLower;
          processedValue = candidateValue;
          processedValueLower = candidateValueLower;
        }
      }
    }

    if (processedValueLower && EXCLUDED_TAG_VALUES.has(processedValueLower)) {
      return;
    }

    const uniqueKey = `${normalizedKeyLower}:::${processedValueLower}`;
    if (seen.has(uniqueKey)) {
      return;
    }
    seen.add(uniqueKey);
    if (normalizedKey) {
      tags.push({ key: normalizedKey, value: processedValue });
    } else {
      tags.push({ value: processedValue });
    }
  };

  const processValue = (value: unknown): void => {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(processValue);
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([entryKey, entryValue]) => {
        if (entryValue && typeof entryValue === 'object' && !Array.isArray(entryValue)) {
          pushTag(entryKey, JSON.stringify(entryValue));
        } else if (Array.isArray(entryValue)) {
          entryValue.forEach((item) => pushTag(entryKey, item));
        } else {
          pushTag(entryKey, entryValue);
        }
      });
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      const looksLikeJson =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'));

      if (looksLikeJson) {
        try {
          const parsed = JSON.parse(trimmed);
          processValue(parsed);
          return;
        } catch {
          // Continue with fallback tokenization when JSON parsing fails
        }
      }

      const tokens = trimmed
        .split(/[\n;,|]+/)
        .map((token) => token.trim())
        .filter(Boolean);

      if (tokens.length === 0) {
        pushTag(null, trimmed);
        return;
      }

      tokens.forEach((token) => {
        const separatorIndex = (() => {
          const colonIndex = token.indexOf(':');
          if (colonIndex !== -1) return colonIndex;
          const equalIndex = token.indexOf('=');
          if (equalIndex !== -1) return equalIndex;
          return -1;
        })();

        if (separatorIndex > -1) {
          const keyPart = token.slice(0, separatorIndex).trim();
          const valuePart = token.slice(separatorIndex + 1).trim();
          if (valuePart) {
            pushTag(keyPart, valuePart);
          } else {
            pushTag(null, keyPart);
          }
        } else {
          pushTag(null, token);
        }
      });
      return;
    }

    pushTag(null, value);
  };

  processValue(rawValue);
  return tags;
};

export const parseNumericValue = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  let str = String(value).trim();
  if (!str) {
    return null;
  }

  // Remove currency symbols and spaces (including NBSP)
  str = str.replace(/[\s\u00A0$₱€£¥]/g, '');

  // Remove any characters except digits, comma, dot, minus
  str = str.replace(/[^0-9,.-]/g, '');

  if (!str) {
    return null;
  }

  const hasComma = str.includes(',');
  const hasDot = str.includes('.');

  if (hasComma && hasDot) {
    // Assume comma is decimal separator, remove dots as thousands
    str = str.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma && !hasDot) {
    // Only comma present, treat as decimal separator
    str = str.replace(/,/g, '.');
  } else if (hasDot) {
    const parts = str.split('.');
    if (parts.length > 2) {
      const decimal = parts.pop();
      str = parts.join('') + '.' + decimal;
    }
  }

  const parsed = parseFloat(str);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getSalePriceValue = (row: TableData): number | null => {
  const candidate =
    row.sale_price ??
    row.salePrice ??
    row.SALE_PRICE ??
    null;
  const parsed = parseNumericValue(candidate);
  if (parsed !== null) {
    return parsed;
  }
  const fallback = row.precio_venta ?? row.precio ?? null;
  return parseNumericValue(fallback);
};

export const getMultiplierValue = (row: TableData, keys: string[]): number | null => {
  const normalizedTargets = new Set(keys.map(normalizeFieldName));
  const isMpuGroup = keys === MPU_KEYS;
  const isMpumGroup = keys === MPUM_KEYS;

  for (const key of keys) {
    if (row[key] !== undefined) {
      const value = parseNumericValue(row[key]);
      if (value !== null) {
        return value;
      }
    }
  }

  for (const [field, rawValue] of Object.entries(row)) {
    const normalizedField = normalizeFieldName(field);
    const matchesExact = normalizedTargets.has(normalizedField);
    const matchesPrefix =
      (isMpuGroup && normalizedField.startsWith('mpu')) ||
      (isMpumGroup && normalizedField.startsWith('mpum'));

    if (matchesExact || matchesPrefix) {
      const parsed = parseNumericValue(rawValue);
      if (parsed !== null) {
        return parsed;
      }
    }
  }

  return null;
};

export const formatCurrencyValue = (value: number | string | null | undefined): string =>
  formatCurrencyARS(value);

export const renderCalculatedPriceBadge = (value: number | null, label?: string) => {
  if (value === null) {
    return React.createElement('span', { className: 'text-gray-400' }, '-');
  }

  return React.createElement('div', { className: 'text-center space-y-1' },
    React.createElement('span', {
      className: 'inline-flex items-center justify-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-900 font-semibold text-sm border border-indigo-100 shadow-sm'
    }, formatCurrencyValue(value)),
    label && React.createElement('span', {
      className: 'block text-xs text-indigo-500 font-semibold tracking-wide uppercase'
    }, label)
  );
};

export const formatMultiplierLabel = (label: string, value: number | null) =>
  value !== null
    ? `${label} × ${value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      })}`
    : undefined;
